import { Field, FieldProps } from "@rjsf/utils";
import React, { ChangeEvent, useEffect, useRef, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import utc from "dayjs/plugin/utc";
import "dayjs/locale/de";

dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.locale("de");

export const DateField: Field = (props: FieldProps) => {
  const { uiSchema } = props;
  const options = uiSchema ? uiSchema["ui:options"] : undefined;
  const [minDate, setMinDate] = useState<Dayjs>();
  const [maxDate, setMaxDate] = useState<Dayjs>();
  const [day, setDay] = useState<number>();
  const [month, setMonth] = useState<number>();
  const [year, setYear] = useState<number>();
  const currentYear = new Date().getFullYear();
  const ref = useRef<HTMLInputElement>(null);
  // Tracks whether the current day/month/year values originate from user input. We must NOT emit
  // onChange when day/month/year are populated by syncing from props.formData — doing so feeds the
  // value straight back into rjsf, which re-renders this field, re-runs the sync effect, and loops
  // ("Maximum update depth exceeded"). Only real edits should propagate.
  const userEdited = useRef(false);

  const changeValue = (e: ChangeEvent<HTMLInputElement>): void => {
    userEdited.current = true;
    switch (e.target.name) {
      case "day":
        setDay(+e.target.value);
        break;
      case "month":
        setMonth(+e.target.value);
        break;
      case "year":
        setYear(+e.target.value);
        break;
    }
  };

  const resetError = (): void => {
    ref.current?.setCustomValidity("");
  };

  useEffect(() => {
    userEdited.current = false;
    if (props.formData) {
      // Parse in UTC: the stored value is a UTC-midnight ISO string (see emit below),
      // so we read back the day/month/year as UTC components — independent of browser TZ.
      let date = dayjs.utc(props.formData);
      if (!date.isValid()) {
        date = dayjs.utc(props.formData, "DD.MM.YYYY");
      }
      if (date.isValid()) {
        setDay(date.date());
        setMonth(date.month() + 1);
        setYear(date.year());
      }
    }
  }, [props.formData]);

  useEffect(() => {
    // Parse in UTC to match the UTC-midnight semantics of currentValue below —
    // otherwise bounds parsed as local midnight are off by one day from the
    // browser's UTC offset (e.g. maxDate rejects its own boundary date).
    if (options && options.minDate) {
      setMinDate(dayjs.utc(options.minDate.toString()));
    }
    if (options && options.maxDate) {
      setMaxDate(dayjs.utc(options.maxDate.toString()));
    }
  }, [options]);

  useEffect(() => {
    // Don't propagate values synced from props.formData — only user edits should fire onChange.
    if (!userEdited.current) return;
    if (day && month && year) {
      // Construct at UTC midnight so toISOString() yields the same calendar day the user picked,
      // regardless of browser timezone. Local-time construction would shift the day for users
      // east of UTC (e.g. CET 00:00 -> 23:00 UTC the previous day).
      const currentValue = dayjs.utc(
        `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`,
        "YYYY-MM-DD",
        true
      );

      if (!currentValue.isValid()) {
        props.onChange(undefined, props.fieldPathId.path);
        return;
      }

      const isoString = currentValue.toISOString();

      if (minDate && maxDate) {
        if (currentValue.diff(minDate) < 0 || currentValue.diff(maxDate) > 0) {
          ref.current?.setCustomValidity(`Das Datum muss zwischen dem ${minDate.format("DD. MMMM YYYY")}
              und dem ${maxDate.format("DD. MMMM YYYY")} liegen.`);
          props.onChange(undefined, props.fieldPathId.path);
        } else {
          props.onChange(isoString, props.fieldPathId.path);
          resetError();
        }
      } else if (minDate) {
        if (currentValue.diff(minDate) < 0) {
          ref.current?.setCustomValidity(`Das Datum muss nach dem ${minDate.format("DD. MMMM YYYY")} liegen.`);
          props.onChange(undefined, props.fieldPathId.path);
        } else {
          props.onChange(isoString, props.fieldPathId.path);
          resetError();
        }
      } else if (maxDate) {
        if (currentValue.diff(maxDate) > 0) {
          ref.current?.setCustomValidity(`Das Datum muss vor dem ${maxDate.format("DD. MMMM YYYY")} liegen.`);
          props.onChange(undefined, props.fieldPathId.path);
        } else {
          props.onChange(isoString, props.fieldPathId.path);
          resetError();
        }
      } else {
        props.onChange(isoString, props.fieldPathId.path);
        resetError();
      }
    }
  }, [day, month, year]);

  return (
    <>
      <div className={"row"}>
        <div className={"col-12"}>
          <div>
            <label className="form-label">{props.name}</label>
          </div>
          <div className={"d-flex flex-wrap mx-0 my-0"}>
            <div className={"me-2"}>
              <label htmlFor={`${props.id}-day`}>Tag</label>
              <input
                ref={ref}
                type={"number"}
                id={`${props.id}-day`}
                name={"day"}
                value={day ?? ""}
                required={props.required}
                min={1}
                max={31}
                className={"form-control"}
                onChange={(e) => changeValue(e)}
              />
            </div>
            <div className={"me-2"}>
              <label htmlFor={`${props.id}-month`}>Monat</label>
              <input
                type={"number"}
                id={`${props.id}-month`}
                name={"month"}
                value={month ?? ""}
                required={props.required}
                min={1}
                max={12}
                className={"form-control"}
                onChange={(e) => changeValue(e)}
              />
            </div>
            <div>
              <label htmlFor={`${props.id}-year`}>Jahr</label>
              <input
                type={"number"}
                id={`${props.id}-year`}
                name={"year"}
                value={year ?? ""}
                required={props.required}
                min={(options?.minYear as number) ?? 2000}
                max={(options?.maxYear as number) ?? currentYear}
                className={"form-control"}
                onChange={(e) => changeValue(e)}
              />
            </div>
          </div>
          <small className={"text-muted form-text mt-0"}>{props.schema.description}</small>
        </div>
      </div>
    </>
  );
};
